import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Box, Container, ThemeProvider, Typography,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';

import EmailAuthForm from '@/pages/Onboarding/components/EmailAuthForm/EmailAuthForm';
import { Product, getTheme, getLob } from '@/utils/productHelpers';
import { activateEmail } from '@/utils/sparkActivationApi';
import { ACCESS_CODE_SESSION_KEY } from '@/utils/constants';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';

interface EmailAuthLocationState {
  accessCode?: string;
  organizationSlug?: string;
}

const EmailAuthPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { state: locationState } = useLocation<EmailAuthLocationState>();
  const posthog = usePostHog();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    posthog?.capture('SignUpEmailLanded');
  }, [posthog]);

  const handleSubmit = async (email: string) => {
    posthog?.capture('SignUpEmailMethodClicked', { email, input: email });
    setLoading(true);
    try {
      if (locationState?.accessCode) {
        sessionStorage.setItem(ACCESS_CODE_SESSION_KEY, locationState.accessCode);
      }

      await activateEmail({
        email,
        product,
        accessCode: locationState?.accessCode,
        organizationSlug: locationState?.organizationSlug,
        lob: getLob(locationState?.organizationSlug),
        phDistinctId: posthog?.get_distinct_id(),
      });

      posthog?.capture('SignUpEmailMagicSent', { email, input: email });

      history.push(`/activate/${product}/check-email`, {
        email,
        accessCode: locationState?.accessCode,
        organizationSlug: locationState?.organizationSlug,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={getTheme(product)}>
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Header />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <Box style={{ marginTop: 32, marginBottom: 40 }}>
            <Typography variant="h2" color="textPrimary" style={{ marginBottom: 8 }}>
              {t('coverage.pre_onboarding.email_auth.title')}
            </Typography>
            <Typography color="textPrimary">
              {t('coverage.pre_onboarding.email_auth.sub_title')}
            </Typography>
          </Box>
          <EmailAuthForm onSubmit={handleSubmit} loading={loading} />
          <ConsentWidget />
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
};

export default EmailAuthPage;
